import { useEffect, useState } from "react";

export default function LandingPage2() {
  const [scrollY, setScrollY] = useState(0);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalMounted, setModalMounted] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const [signatureCanvas, setSignatureCanvas] = useState(null);
  const [activeFaq, setActiveFaq] = useState(null);
  const MODAL_DISTANCE = 400;

  const toggleFaq = (index) => {
    setActiveFaq(activeFaq === index ? null : index);
  };

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const showModal = scrollY > 0 && scrollY < MODAL_DISTANCE;
  const showOverlay = scrollY >= MODAL_DISTANCE;

  useEffect(() => {
    let timeoutId;

    if (showModal) {
      setModalMounted(true);
      const raf = requestAnimationFrame(() => setModalVisible(true));
      return () => cancelAnimationFrame(raf);
    }

    setModalVisible(false);
    timeoutId = setTimeout(() => {
      setModalMounted(false);
    }, 260);

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [showModal]);

  useEffect(() => {
    const canvas = signatureCanvas;
    if (!canvas) return;

    const resizeCanvas = () => {
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width;
      canvas.height = rect.height;
      const ctx = canvas.getContext("2d");
      ctx.strokeStyle = "#000";
      ctx.lineWidth = 2;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
    };

    resizeCanvas();
    window.addEventListener("resize", resizeCanvas);
    return () => window.removeEventListener("resize", resizeCanvas);
  }, [signatureCanvas]);

  useEffect(() => {
    const canvas = signatureCanvas;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    ctx.strokeStyle = "#000";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";

    let drawing = false;

    const getCoordinates = (e) => {
      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;
      
      if (e.touches && e.touches.length > 0) {
        return {
          x: (e.touches[0].clientX - rect.left) * scaleX,
          y: (e.touches[0].clientY - rect.top) * scaleY,
        };
      }
      return {
        x: (e.clientX - rect.left) * scaleX,
        y: (e.clientY - rect.top) * scaleY,
      };
    };

    const startDrawing = (e) => {
      e.preventDefault();
      drawing = true;
      setIsDrawing(true);
      const coords = getCoordinates(e);
      ctx.beginPath();
      ctx.moveTo(coords.x, coords.y);
    };

    const draw = (e) => {
      e.preventDefault();
      if (!drawing) return;
      const coords = getCoordinates(e);
      ctx.lineTo(coords.x, coords.y);
      ctx.stroke();
    };

    const stopDrawing = () => {
      drawing = false;
      setIsDrawing(false);
    };

    canvas.addEventListener("mousedown", startDrawing);
    canvas.addEventListener("mousemove", draw);
    canvas.addEventListener("mouseup", stopDrawing);
    canvas.addEventListener("mouseleave", stopDrawing);
    canvas.addEventListener("touchstart", startDrawing);
    canvas.addEventListener("touchmove", draw);
    canvas.addEventListener("touchend", stopDrawing);

    return () => {
      canvas.removeEventListener("mousedown", startDrawing);
      canvas.removeEventListener("mousemove", draw);
      canvas.removeEventListener("mouseup", stopDrawing);
      canvas.removeEventListener("mouseleave", stopDrawing);
      canvas.removeEventListener("touchstart", startDrawing);
      canvas.removeEventListener("touchmove", draw);
      canvas.removeEventListener("touchend", stopDrawing);
    };
  }, [signatureCanvas]);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  };

  const heroStyles = {
    position: "fixed",
    inset: 0,
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "left",
    padding: "4rem",
    background: "url(/checkers2.png)",
    color: "#111",
    zIndex: -1, 
  };

  const navMenuStyles = {
    position: "absolute",
    top: "2rem",
    left: "50%",
    transform: "translateX(-50%)",
    display: "flex",
    flexDirection: "row",
    gap: "0.5rem",
    zIndex: 10,
    flexWrap: "wrap",
    justifyContent: "center",
  };

  const navMenuItemStyles = {
    padding: "0.6rem 1.5rem",
    backgroundColor: "rgba(255, 255, 255, 0.9)",
    color: "#0b4bc1",
    borderRadius: "8px",
    fontSize: "0.9rem",
    fontWeight: 600,
    cursor: "pointer",
    border: "none",
    fontFamily: "Tektur, sans-serif",
    textAlign: "center",
    transition: "all 0.2s ease",
    boxShadow: "0 2px 8px rgba(15, 23, 42, 0.1)",
    whiteSpace: "nowrap",
  };

  const heroTextStyles = {
    fontFamily: "Tektur, sans-serif",
    fontSize: "clamp(3rem, 10vw, 8rem)",
    fontWeight: 700,
    textAlign: "left",
    lineHeight: 1,
    color:"#154BB7"
  };

  const overlayStyles = {
    position: "relative",
    marginTop: "100vh", 
    zIndex: 1,
    backgroundColor: showOverlay ? "#fff" : "transparent",
    transition: "background-color 250ms ease",
    padding: "6rem 5vw 8rem",
    boxSizing: "border-box",
  };

  const contentContainerStyles = {
    maxWidth: "1100px",
    margin: "0 auto",
    fontFamily: '"IBM Plex Sans", sans-serif',
    color: "#0f172a",
  };

  const sectionTitleStyles = {
    fontFamily: "Tektur, sans-serif",
    fontSize: "clamp(2rem, 4vw, 3.25rem)",
    textTransform: "uppercase",
    textAlign: "center",
    color: "#0b4bc1",
    letterSpacing: "0.08em",
    fontWeight:"bold",
  };

  const sectionSubtitleStyles = {
    marginTop: "1.5rem",
    maxWidth: "760px",
    marginLeft: "auto",
    marginRight: "auto",
    textAlign: "center",
    fontSize: "1.1rem",
    lineHeight: 1.6,
    color: "#1e293b",
  };

  const problemsGridStyles = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: "1.5rem",
    marginTop: "3rem",
  };

  const cardStyles = {
    backgroundColor: "#fff",
    //borderRadius: "20px",
    border: "1px solid #e2e8f0",
    boxShadow: "0 5px 10px rgba(6, 11, 22, 0.08)",
    padding: "2.5rem 2rem",
    textAlign: "center",
    minHeight: "250px",
  };

  const cardIconStyles = {
    width: "3vw",
    height: "3vw",
    borderRadius: "18px",
    margin: "0 auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "center", 
  };

  const cardIconImageStyles = {
    width: "2vw",
    height: "2vw",
    objectFit: "contain",
  };

  const cardTitleStyles = {
    marginTop: "1.25rem",
    fontSize: "1.1rem",
    fontWeight: 700,
  };

  const cardTextStyles = {
    marginTop: "0.75rem",
    fontSize: "0.98rem",
    lineHeight: 1.5,
    color: "#475569",
  };

  const dividerStyles = {
    height: "1px",
    width: "100%",
    //background: "linear-gradient(90deg, rgba(11,99,246,0) 0%, rgba(11,99,246,0.4) 50%, rgba(11,99,246,0) 100%)",
    margin: "2rem 0 1rem",
  };

  const solutionTitleStyles = { 
    fontSize: "clamp(1.8rem, 3vw, 2.5rem)",
    textAlign: "center",
    color: "#0b4bc1",
    fontWeight:"bold",
  };

  const solutionTextStyles = {
    marginTop: "1rem",
    textAlign: "center",
    fontSize: "1.05rem",
    lineHeight: 1.6,
    color: "#1e293b",
  };

  const howSectionStyles = { 
    padding: "5rem 4vw 5rem", 
    backgroundColor: "#e6ecf9",
    textAlign: "center",
  };

  const howLabelStyles = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "0.6rem 2.8rem",
    borderRadius: "999px",
    backgroundColor: "#0b4bc1",
    color: "#fff",
    fontWeight: 600,
    fontSize: "1rem",
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    fontFamily: "inherit",
  };

  const howTitleStyles = {
    marginTop: "2rem",
    fontSize: "clamp(2rem, 4vw, 3.2rem)",
    textTransform: "uppercase",
    fontWeight: "bold",
    color: "#0b4bc1",
    letterSpacing: "0.08em",
    fontFamily: "Tektur, sans-serif",
  };

  const howSubtitleStyles = {
    marginTop: "0.75rem",
    fontSize: "1.1rem",
    color: "#1f2a44",
    lineHeight: 1.6,
  };

  const stepsGridStyles = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "2rem",
    marginTop: "3rem",
    textAlign: "center",
  };

  const stepCardStyles = {
    padding: "0 1rem",
  };

  const stepBadgeStyles = {
    width: "80px",
    height: "80px",
    borderRadius: "999px",
    backgroundColor: "#0b4bc1",
    color: "#fff",
    fontSize: "2rem",
    fontWeight: 700,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto",
    boxShadow: "0 12px 20px rgba(11, 75, 193, 0.25)",
  };

  const stepTitleStyles = {
    marginTop: "1.25rem",
    fontSize: "1.05rem",
    fontWeight: 700,
    color: "#0f172a",
  };

  const stepTextStyles = {
    marginTop: "0.5rem",
    fontSize: "0.95rem",
    color: "#1f2a44",
    lineHeight: 1.5,
  };

  const howSteps = [
    {
      title: "Настройка",
      text: "Один раз согласуем и разработаем шаблоны документов под вашу организацию.",
    },
    {
      title: "Формирование",
      text: "Сервис автоматически создаёт документы по утверждённым шаблонам.",
    },
    {
      title: "Подпись ученика",
      text: "Документы подписываются онлайн — пальцем на экране или мышью.",
    },
    {
      title: "Хранение",
      text: "Документы сохраняются в системе и доступны для проверки в любой момент.",
    },
  ];

  const benefitsSectionStyles = {
    padding: "5rem 5vw 6rem",
    backgroundColor: "#0b4bc1",
    color: "#fff",
    textAlign: "center",
  };

  const benefitsTitleStyles = {
    fontFamily: "Tektur",
    textTransform: "uppercase",
    fontSize: "clamp(2rem, 4vw, 3.2rem)",
    letterSpacing: "0.1em",
    fontWeight: 700,
  };

  const benefitsSubtitleStyles = {
    marginTop: "1rem",
    fontSize: "1.1rem",
    opacity: 0.85,
  };

  const benefitsGridStyles = {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: "3rem",
    marginTop: "3.5rem",
    textAlign: "left",
  };

  const benefitItemTitleStyles = {
    fontSize: "2rem",
    fontWeight: 700,
    marginBottom: "0.4rem",
  };

  const benefitItemTextStyles = {
    fontSize: "1.05rem",
    lineHeight: 1.5,
    opacity: 0.9,
  };

  const benefitCardBaseStyles = {
    maxWidth: "520px",
  };

  const benefitItems = [
    {
      title: "Готовность к проверкам",
      text: "Весь пакет документов всегда в порядке и готов к проверкам Рособрнадзора.",
    },
    {
      title: "Юридическая корректность",
      text: "Документы соответствуют законодательству РФ и обновляются при изменениях.",
    },
    {
      title: "Системность",
      text: "Все документы хранятся в одном месте, с удобным поиском и доступом для сотрудников.",
    },
    {
      title: "Экономия времени",
      text: "Автоматизация сокращает рутину и снижает количество ошибок до нуля.",
    },
    {
      title: "Оптимизация затрат",
      text: "Не нужно тратить деньги на юриста и ручную сверку шаблонов.",
    },
  ];

  const benefitAfterwordTitleStyles = {
    marginTop: "3.5rem",
    fontSize: "1.7rem",
    fontWeight: 700,
  };

  const benefitAfterwordTextStyles = {
    marginTop: "0.5rem",
    fontSize: "1.05rem",
    opacity: 0.9,
    lineHeight: 1.6,
  };

  const legalSectionStyles = {
    backgroundColor: "#e6ecf9",
    padding: "5rem 4vw 6rem",
    textAlign: "center",
  };

  const legalLabelStyles = {
    display: "inline-flex",
    padding: "0.5rem 2.5rem",
    borderRadius: "999px",
    backgroundColor: "#0b4bc1",
    color: "#fff",
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    fontWeight: 600,
    fontSize: "0.95rem",
    fontFamily: "Tektur, sans-serif",
  };

  const legalTitleStyles = {
    marginTop: "2rem",
    fontFamily: "Tektur",
    fontWeight: 700,
    textTransform: "uppercase",
    fontSize: "clamp(2rem, 4vw, 3.1rem)",
    color: "#0b4bc1",
    letterSpacing: "0.08em",
  };

  const legalSubtitleStyles = {
    marginTop: "1rem",
    fontSize: "1.05rem",
    lineHeight: 1.6,
    color: "#1f2a44", 
  };

  const legalCardStyles = {
    marginTop: "2.5rem",
    backgroundColor: "#fff",
    borderRadius: "24px",
    padding: "2.5rem",
    maxWidth: "900px",
    marginLeft: "auto",
    marginRight: "auto",
    textAlign: "left",
    boxShadow: "0 18px 35px rgba(15, 23, 42, 0.1)",
  };

  const legalListStyles = {
    listStyle: "none",
    padding: 0,
    margin: 0,
    display: "flex",
    flexDirection: "column",
    gap: "1.75rem",
  };

  const legalListItemStyles = {
    display: "grid",
    gridTemplateColumns: "56px 1fr",
    gap: "1.25rem",
    alignItems: "flex-start",
    fontSize: "1rem",
    lineHeight: 1.6,
    color: "#1f2a44",
    fontFamily: "Tektur, sans-serif",
  };

  const legalIconStyles = {
    display: "block",
    width: "3rem",
    height: "3rem",
    borderRadius: "50%",
    background: 'url("/free-icon-font-check-circle.png")',
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
  };

  const legalResultTitleStyles = {
    marginTop: "3.5rem", 
    fontSize: "2rem",
    color: "#0b4bc1",
  };

  const legalResultTextStyles = {
    marginTop: "0.75rem",
    fontSize: "1.05rem",
    color: "#1f2a44", 
  };

  const legalPoints = [
    {
      text: "Пункт 2 статьи 434 Гражданского кодекса РФ допускает заключение договоров в письменной форме, в том числе в электронном виде.",
    },
    {
      text: "Подписание осуществляется с использованием простой электронной подписи (ПЭП), которая является законным аналогом собственноручной подписи.",
    },
    {
      text: "В каждый документ автоматически включается соглашение об использовании электронной подписи и её реквизиты.",
    },
  ];

  const problemCards = [
    {
      iconSrc: "/free-icon-font-clock-three.png",
      iconAlt: "Иконка часов",
      title: "Временные затраты",
      text: "Ручная работа на подготовку договоров и приказов отнимает много времени",
    },
    {
      iconSrc: "/free-icon-font-file-exclamation.png",
      iconAlt: "Иконка файла с восклицательным знаком",
      title: "Риск ошибок",
      text: "Любая неточность в формулировках вызывает вопросы при проверке",
    },
    {
      iconSrc: "/free-icon-font-user-check.png",
      iconAlt: "Иконка пользователя",
      title: "Личный контроль",
      text: "Владельцу приходится держать под контролем каждый документ.",
    },
  ];

  const modalWrapperStyles = {
    position: "fixed",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "1.5rem",
    zIndex: 2,
    pointerEvents: showModal ? "auto" : "none",
    background: modalVisible
      ? "rgba(15, 23, 42, 0.15)"
      : "rgba(15, 23, 42, 0)",
    backdropFilter: modalVisible ? "blur(5px)" : "blur(0px)",
    transition:
      "opacity 200ms ease, backdrop-filter 250ms ease, background 250ms ease",
    opacity: modalVisible ? 1 : 0,
  };

  const modalStyles = {
    width: "min(90vw, 580px)",
    padding: "3rem",
    borderRadius: "30px",
    backgroundColor: "#fff",
    boxShadow: "0 30px 60px rgba(15, 23, 42, 0.15)",
    border: "1px solid #e2e8f0",
    textAlign: "center", 
    margin: "0 auto",
    transition: "opacity 220ms ease, transform 220ms ease",
    opacity: modalVisible ? 1 : 0,
    transform: modalVisible ? "translateY(0)" : "translateY(32px)",
  };

  const headingStyles = {
    fontSize: "1.75rem",
    fontWeight: 700,
    color: "#0f172a",
    lineHeight: 1.3,
    textTransform: "uppercase",
    letterSpacing: "0.03em",
    margin: 0,
  };

  const leadStyles = {
    marginTop: "1.5rem",
    fontSize: "1.05rem",
    lineHeight: 1.6,
    color: "#334155",
  };

  const dashedBoxStyles = {
    marginTop: "1.5rem",
    padding: "1.5rem",
    borderRadius: "18px",
    border: "2px dashed #94a3b8",
    fontSize: "1rem",
    lineHeight: 1.5,
    color: "#0f172a",
    backgroundColor: "#f8fafc",
  };

  const ctaStyles = {
    marginTop: "2rem",
    padding: "0.9rem 3.5rem",
    borderRadius: "999px",
    border: "none",
    fontWeight: 600,
    fontSize: "1rem",
    backgroundColor: "#0b63f6",
    color: "#fff",
    cursor: "pointer",
    boxShadow: "0 12px 30px rgba(11, 99, 246, 0.3)",
  };

  const demoHeaderButtonStyles = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "0.75rem 2.5rem",
    borderRadius: "8px",
    backgroundColor: "#0b4bc1",
    color: "#fff",
    fontWeight: 600,
    fontSize: "0.95rem",
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    border: "none",
    cursor: "pointer",
    fontFamily: "Tektur, sans-serif",
    margin: "0 auto",
  };

  const demoSectionStyles = {
    padding: "6rem 5vw 6rem",
    backgroundColor: "#fff",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  };

  const demoWrapperStyles = {
    maxWidth: "1200px",
    width: "100%",
    backgroundColor: "#e6ecf9",
    borderRadius: "32px",
    padding: "4rem 3rem",
    boxShadow: "0 4px 12px rgba(15, 23, 42, 0.08)",
  };

  const demoPanelStyles = {
    width: "100%",
    padding: 0,
  };

  const demoTitleStyles = {
    fontFamily: "Tektur, sans-serif",
    fontSize: "clamp(1.8rem, 3vw, 2.5rem)",
    textTransform: "uppercase",
    textAlign: "center",
    color: "#0b4bc1",
    fontWeight: "bold",
    letterSpacing: "0.08em",
    marginBottom: "1rem",
  };

  const demoSubtitleStyles = {
    textAlign: "center",
    fontSize: "1.05rem",
    color: "#1e293b",
    marginBottom: "3rem",
    lineHeight: 1.6, 
  };

  const demoContentGridStyles = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
    gap: "3rem",
    alignItems: "flex-start",
  };

  const demoLeftColumnStyles = {
    display: "flex",
    flexDirection: "column",
  };

  const demoLeftTitleStyles = {
    fontSize: "1.3rem",
    fontWeight: 700,
    color: "#0f172a",
    marginBottom: "1.5rem",
    fontFamily: "Tektur, sans-serif",
  };

  const demoStepCardStyles = {
    backgroundColor: "#fff",
    borderRadius: "12px",
    padding: "0.5rem",
    marginBottom: "1rem",
    display: "flex",
    alignItems: "center",
    gap: "1rem",
    boxShadow: "0 2px 8px rgba(15, 23, 42, 0.08)",
  };

  const demoStepNumberStyles = {
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    backgroundColor: "#0b4bc1",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 700,
    fontSize: "1.2rem",
    flexShrink: 0,
    fontFamily: "Tektur, sans-serif",
  };

  const demoStepContentStyles = {
    flex: 1,
  };

  const demoStepTitleStyles = {
    fontSize: "1.05rem",
    fontWeight: 700,
    color: "#0f172a",
    marginBottom: "0.5rem", 
  };

  const demoStepTextStyles = {
    fontSize: "0.95rem",
    color: "#475569",
    lineHeight: 1.5, 
  };

  const demoButtonStyles = {
    marginTop: "1.5rem",
    padding: "1rem 2rem",
    borderRadius: "8px",
    backgroundColor: "#0b4bc1",
    color: "#fff",
    fontWeight: 600,
    fontSize: "1rem",
    border: "none",
    cursor: "pointer",
    width: "100%",
    fontFamily: "Tektur, sans-serif",
  };

  const demoButtonTextStyles = {
    marginTop: "0.75rem",
    fontSize: "0.9rem",
    color: "#475569",
    lineHeight: 1.5,
    textAlign: "center", 
  };

  const demoRightColumnStyles = {
    display: "flex",
    flexDirection: "column",
    backgroundColor: "#fff",
    borderRadius: "16px",
    padding: "2rem",
    boxShadow: "0 2px 8px rgba(15, 23, 42, 0.08)",
  };

  const demoRightTitleStyles = {
    fontSize: "1.3rem",
    fontWeight: 700,
    color: "#0f172a",
    marginBottom: "0.75rem",
    fontFamily: "Tektur, sans-serif",
  };

  const demoRightInstructionStyles = {
    fontSize: "0.95rem",
    color: "#475569",
    marginBottom: "1.5rem",
    lineHeight: 1.5,
    fontFamily: "Tektur, sans-serif",
  };

  const demoSignatureCanvasStyles = {
    width: "100%",
    height: "200px",
    border: "1px solid #cbd5e1",
    borderRadius: "8px",
    backgroundColor: "#fff",
    cursor: "crosshair",
    touchAction: "none",
  };

  const demoDisclaimerStyles = {
    marginTop: "1rem",
    fontSize: "0.85rem",
    color: "#64748b",
    lineHeight: 1.5,
    fontFamily: "Tektur, sans-serif",
  };

  const targetSectionStyles = {
    backgroundColor: "#e6ecf9",
    padding: "6rem 5vw 6rem",
    textAlign: "center",
  };

  const targetHeaderButtonStyles = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "0.75rem 2.5rem",
    borderRadius: "8px",
    backgroundColor: "#0b4bc1",
    color: "#fff",
    fontWeight: 600,
    fontSize: "0.95rem",
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    border: "none",
    cursor: "pointer",
    fontFamily: "Tektur, sans-serif",
    marginBottom: "2rem",
  };

  const targetTitleStyles = {
    fontFamily: "Tektur, sans-serif",
    fontSize: "clamp(2rem, 4vw, 3.2rem)",
    textTransform: "uppercase",
    textAlign: "center",
    color: "#0b4bc1",
    fontWeight: "bold",
    letterSpacing: "0.08em",
    marginBottom: "1rem",
  };

  const targetSubtitleStyles = {
    textAlign: "center",
    fontSize: "1.05rem",
    color: "#1e293b",
    marginBottom: "3rem",
    lineHeight: 1.6,
    fontFamily: "Tektur, sans-serif",
    maxWidth: "800px",
    marginLeft: "auto",
    marginRight: "auto",
  };

  const targetCardsGridStyles = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    gap: "2rem",
    marginTop: "3rem",
    maxWidth: "1200px",
    marginLeft: "auto",
    marginRight: "auto",
  };

  const targetCardStyles = {
    backgroundColor: "#fff",
    borderRadius: "16px",
    padding: "2rem",
    textAlign: "left",
    boxShadow: "0 4px 12px rgba(15, 23, 42, 0.1)",
    position: "relative",
  };

  const targetCardPopularTagStyles = {
    position: "absolute",
    top: "-12px",
    left: "50%",
    transform: "translateX(-50%)",
    padding: "0.4rem 1.5rem",
    borderRadius: "999px",
    backgroundColor: "#0b4bc1",
    color: "#fff",
    fontWeight: 600,
    fontSize: "0.85rem",
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    fontFamily: "Tektur, sans-serif",
  };

  const targetCardTitleStyles = {
    fontSize: "1.2rem",
    fontWeight: 700,
    color: "#0f172a",
    marginBottom: "0.75rem",
    fontFamily: "Tektur, sans-serif",
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
  };

  const targetCardIconStyles = {
    width: "24px",
    height: "24px",
    flexShrink: 0,
  };

  const targetCardDescriptionStyles = {
    fontSize: "0.95rem",
    color: "#475569",
    marginBottom: "1.5rem",
    lineHeight: 1.5, 
  };

  const targetCardListStyles = {
    listStyle: "none",
    padding: 0,
    margin: 0,
    display: "flex",
    flexDirection: "column",
    gap: "0.75rem",
  };

  const targetCardListItemStyles = {
    fontSize: "0.9rem",
    color: "#1f2a44",
    lineHeight: 1.5, 
    display: "flex",
    alignItems: "flex-start",
    gap: "0.5rem",
  };

  const targetCardListIconStyles = {
    width: "18px",
    height: "18px",
    flexShrink: 0,
    marginTop: "0.15rem",
  };

  const targetResultWrapperStyles = {
    marginTop: "4rem",
    backgroundColor: "#fff",
    borderRadius: "16px",
    padding: "2.5rem",
    maxWidth: "1200px",
    marginLeft: "auto",
    marginRight: "auto",
    textAlign: "center",
    boxShadow: "0 4px 12px rgba(15, 23, 42, 0.1)",
  };

  const targetResultTitleStyles = { 
    fontSize: "clamp(1.8rem, 3vw, 2.5rem)",
    textTransform: "uppercase",
    color: "#0b4bc1",
    fontWeight: "bold",
    letterSpacing: "0.08em",
    marginBottom: "0.75rem",
  };

  const targetResultTextStyles = {
    fontSize: "1.05rem",
    color: "#1e293b",
    lineHeight: 1.6, 
  };

  const targetCards = [
    {
      title: "Онлайн школам и образовательным центрам",
      description: "Автоматизация документооборота для дистанционного обучения",
      features: [
        "Автоматическое формирование договоров",
        "Электронное подписание документов",
        "Централизованное хранение",
        "Готовность к проверкам Рособрнадзора",
      ],
      popular: false,
    },
    {
      title: "ИП, оказывающим образовательные услуги",
      description: "Простое решение для индивидуальных предпринимателей",
      features: [
        "Шаблоны под разные виды деятельности",
        "Работа с лицензией и без неё",
        "Юридическая корректность документов",
        "Экономия времени и ресурсов",
      ],
      popular: true,
    },
    {
      title: "Всем, кто хочет быть готовым к проверке",
      description: "Без стресса и лишних хлопот",
      features: [
        "Полная готовность к любым проверкам",
        "Соответствие всем требованиям",
        "Профессиональная поддержка",
        "Минимизация рисков",
      ],
      popular: false,
    },
  ];

  const navItems = [
    { id: "problems", label: "Проблемы" },
    { id: "how-it-works", label: "Как это работает" },
    { id: "benefits", label: "Преимущества" },
    { id: "legal", label: "Юридическая значимость" },
    { id: "demo", label: "Демонстрация" },
    { id: "target", label: "Кому подходит" },
    { id: "faq", label: "FAQ" },
  ];

  const faqSectionStyles = {
    padding: "6rem 5vw 6rem",
    backgroundColor: "#fff",
    textAlign: "center",
  };

  const faqHeaderButtonStyles = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "0.75rem 2.5rem",
    borderRadius: "999px",
    backgroundColor: "#0b4bc1",
    color: "#fff",
    fontWeight: 600,
    fontSize: "0.95rem",
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    border: "none",
    cursor: "pointer",
    fontFamily: "Tektur, sans-serif",
    marginBottom: "2rem",
  };

  const faqTitleStyles = {
    fontFamily: "Tektur, sans-serif",
    fontSize: "clamp(2rem, 4vw, 3.2rem)",
    textTransform: "uppercase",
    textAlign: "center",
    color: "#0b4bc1",
    fontWeight: "bold",
    letterSpacing: "0.08em",
    marginBottom: "1rem",
  };

  const faqSubtitleStyles = {
    textAlign: "center",
    fontSize: "1.05rem",
    color: "#1e293b",
    marginBottom: "3rem",
    lineHeight: 1.6, 
    maxWidth: "800px",
    marginLeft: "auto",
    marginRight: "auto",
  };

  const faqAccordionStyles = {
    maxWidth: "900px",
    marginLeft: "auto",
    marginRight: "auto",
    display: "flex",
    flexDirection: "column",
    gap: "1rem",
  };

  const faqItemStyles = {
    backgroundColor: "#f1f5f9",
    borderRadius: "12px",
    overflow: "hidden",
    boxShadow: "0 2px 8px rgba(15, 23, 42, 0.08)",
    transition: "all 0.3s ease",
  };

  const faqQuestionStyles = (isActive) => ({
    backgroundColor: isActive ? "#0b4bc1" : "#f1f5f9",
    padding: "1.5rem 2rem",
    cursor: "pointer",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    fontWeight: 600,
    fontSize: "1.1rem",
    color: isActive ? "#fff" : "#1e293b", 
    transition: "all 0.3s ease",
  });

  const faqIconStyles = (isActive) => ({
    fontSize: "1.2rem",
    transition: "transform 0.3s ease",
    transform: isActive ? "rotate(45deg)" : "rotate(0deg)",
  });

  const faqAnswerStyles = (isActive) => ({
    maxHeight: isActive ? "500px" : "0",
    overflow: "hidden",
    transition: "all 0.3s ease",
    padding: isActive ? "1.5rem 2rem" : "0 2rem",
  });

  const faqAnswerTextStyles = {
    color: "#64748b",
    lineHeight: 1.6,
    fontSize: "1rem", 
    margin: 0,
  };

  const faqCtaStyles = {
    marginTop: "3rem",
    backgroundColor: "#f1f5f9",
    borderRadius: "20px",
    padding: "2.5rem",
    maxWidth: "900px",
    marginLeft: "auto",
    marginRight: "auto",
    textAlign: "center",
  };

  const faqCtaTitleStyles = {
    fontSize: "1.3rem",
    fontWeight: 700,
    color: "#0f172a",
    marginBottom: "1rem", 
  };

  const faqCtaTextStyles = {
    fontSize: "1rem",
    color: "#1e293b",
    marginBottom: "1.5rem",
    lineHeight: 1.6, 
  };

  const faqCtaButtonStyles = {
    padding: "1rem 2.5rem",
    borderRadius: "8px",
    backgroundColor: "#0b4bc1",
    color: "#fff",
    fontWeight: 600,
    fontSize: "1rem",
    border: "none",
    cursor: "pointer", 
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  };

  const faqItems = [
    {
      question: "Можно ли использовать сервис, если у нас нет лицензии?",
      answer: "Да. Мы подбираем шаблоны под формат вашей деятельности — с лицензией или без неё.",
    },
    {
      question: "Насколько безопасно хранить документы онлайн?",
      answer: "Все данные размещаются на защищённом сервере в России с резервным копированием и шифрованием.",
    },
    {
      question: "Как подписываются документы?",
      answer: "Подпись ставится онлайн — пальцем или мышью. Это простая электронная подпись, признанная законом.",
    },
    {
      question: "Можно ли интегрировать сервис с CRM или платформой обучения?",
      answer: "Да, поддерживаем интеграции через API.",
    },
    {
      question: "Что делать, если нужно изменить шаблон документа?",
      answer: "Мы настраиваем шаблоны индивидуально и при необходимости обновляем под ваши требования.",
    },
    {
      question: "Сколько стоит подключение?",
      answer: "Вы можете протестировать сервис бесплатно. После демо подберём тариф под количество учеников и документов.",
    },
  ];

  const demoSteps = [
    {
      number: 1,
      title: "Выбираете документ",
      text: "Выбираете нужный тип документа из списка",
    },
    {
      number: 2,
      title: "Заполняете ключевые данные",
      text: "Вводите информацию в поля формы",
    },
    {
      number: 3,
      title: "Подписываете онлайн",
      text: "Ставите подпись пальцем или мышью",
    },
    {
      number: 4,
      title: "Получаете готовый документ",
      text: "Документ сохраняется в системе",
    },
  ];

  const allStyles = {
    fontFamily: '"IBM Plex Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    fontOpticalSizing: "auto", 
    fontStyle: "normal"
  }

  return (
    <div className="landing-page-2" style={allStyles}>
      <style>
        @import url('https://fonts.googleapis.com/css2?family=Tektur:wght@400..900&display=swap');
        @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:ital,wght@0,100..700;1,100..700&display=swap');
      </style>
      <style dangerouslySetInnerHTML={{
        __html: `
          .landing-page-2 {
            font-family: "IBM Plex Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif !important;
          }
          .landing-page-2 p:not([style*="Tektur"]),
          .landing-page-2 span:not([style*="Tektur"]),
          .landing-page-2 div:not([style*="Tektur"]),
          .landing-page-2 a:not([style*="Tektur"]),
          .landing-page-2 button:not([style*="Tektur"]),
          .landing-page-2 input:not([style*="Tektur"]),
          .landing-page-2 textarea:not([style*="Tektur"]),
          .landing-page-2 select:not([style*="Tektur"]),
          .landing-page-2 label:not([style*="Tektur"]),
          .landing-page-2 li:not([style*="Tektur"]),
          .landing-page-2 ul:not([style*="Tektur"]),
          .landing-page-2 h1:not([style*="Tektur"]),
          .landing-page-2 h2:not([style*="Tektur"]),
          .landing-page-2 h3:not([style*="Tektur"]),
          .landing-page-2 h4:not([style*="Tektur"]),
          .landing-page-2 h5:not([style*="Tektur"]),
          .landing-page-2 h6:not([style*="Tektur"]) {
            font-family: "IBM Plex Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif !important;
          }
        `
      }} />
      <section style={heroStyles}>
        <nav style={navMenuStyles}>
          {navItems.map((item) => (
            <button
              key={item.id}
              style={navMenuItemStyles}
              onClick={() => scrollToSection(item.id)}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = "#0b4bc1";
                e.target.style.color = "#fff";
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = "rgba(255, 255, 255, 0.9)";
                e.target.style.color = "#0b4bc1";
              }}
            >
              {item.label}
            </button>
          ))}
        </nav>
        <p style={heroTextStyles}>автоматизация<br />
        документооборота</p>
      </section>

      <section id="problems" style={overlayStyles}>
        <div style={contentContainerStyles}>
          <p style={sectionTitleStyles}>проблемы, которые мы решаем</p>
          <p style={sectionSubtitleStyles}>
            Каждый ученик онлайн-школы — это десятки документов. Договоры,
            приказы, журналы, ведомости… Мы освобождаем вас от рутины,
            автоматизируя весь учебный документооборот.
          </p>

          <div style={problemsGridStyles}>
            {problemCards.map((card) => (
              <article key={card.title} style={cardStyles}>
                <div style={cardIconStyles} aria-hidden="true">
                  <img
                    src={card.iconSrc}
                    alt={card.iconAlt}
                    style={cardIconImageStyles}
                  />
                </div>
                <p style={cardTitleStyles}>{card.title}</p>
                <p style={cardTextStyles}>{card.text}</p>
              </article>
            ))}
          </div>

          <div style={dividerStyles} />

          <p style={solutionTitleStyles}>Наш сервис решает эти проблемы</p>
          <p style={solutionTextStyles}>
          Он формирует юридически корректные документы по утверждённым шаблонам, минимизирует ошибки и освобождает команду от рутины
          </p>

        </div>
      </section>

      <section id="how-it-works" style={howSectionStyles}>
        <span style={howLabelStyles}>как это работает</span>
        <p style={howTitleStyles}>этапы работы с сервисом</p>
        <p style={howSubtitleStyles}>
          Простой и понятный процесс автоматизации документооборота
        </p>

        <div style={stepsGridStyles}>
          {howSteps.map((step, idx) => (
            <article key={step.title} style={stepCardStyles}>
              <div style={stepBadgeStyles}>{idx + 1}</div>
              <p style={stepTitleStyles}>{step.title}</p>
              <p style={stepTextStyles}>{step.text}</p>
            </article>
          ))}
        </div>
      </section>

      <section id="benefits" style={benefitsSectionStyles}>
        <p style={benefitsTitleStyles}>преимущества для вашего бизнеса</p>
        <p style={benefitsSubtitleStyles}>
          Автоматизация документооборота приносит реальную пользу
        </p>

        <div style={benefitsGridStyles}>
          {benefitItems.map((item, idx) => {
            const isLast = idx === benefitItems.length - 1;

            if (isLast) {
              return (
                <article
                  key={item.title}
                  style={{
                    ...benefitCardBaseStyles,
                    gridColumn: "1 / span 2",
                    textAlign: "center",
                    margin: "0 auto",
                  }}
                >
                  <p style={benefitItemTitleStyles}>{item.title}</p>
                  <p style={benefitItemTextStyles}>{item.text}</p>
                </article>
              );
            }

            const alignedStyles =
              idx % 2 === 0
                ? { textAlign: "right", marginLeft: "auto" }
                : { textAlign: "left", marginRight: "auto" };

            return (
              <article
                key={item.title}
                style={{ ...benefitCardBaseStyles, ...alignedStyles }}
              >
                <p style={benefitItemTitleStyles}>{item.title}</p>
                <p style={benefitItemTextStyles}>{item.text}</p>
              </article>
            );
          })}
        </div>

        <p style={benefitAfterwordTitleStyles}>Результат</p>
        <p style={benefitAfterwordTextStyles}>
          Сервис помогает сократить время на оформление документов с нескольких
          часов до пары минут — без ошибок и повторных проверок.
        </p>
      </section>

      <section id="legal" style={legalSectionStyles}>
        <span style={legalLabelStyles}>юридическая значимость</span>
        <p style={legalTitleStyles}>
          подписание документов — безопасно и юридически корректно
        </p>
        <p style={legalSubtitleStyles}>
          Договоры и дополнительные соглашения, подписанные через наш сервис,
          обладают такой же юридической силой, как и бумажные документы
        </p>

        <div style={legalCardStyles}>
          <ul style={legalListStyles}>
            {legalPoints.map((item) => (
              <li key={item.text} style={legalListItemStyles}>
                <span style={legalIconStyles}></span>
                <span>{item.text}</span>
              </li>
            ))}
          </ul>
        </div>

        <p style={legalResultTitleStyles}>Результат</p>
        <p style={legalResultTextStyles}>
          Документы юридически значимы и готовы к любой проверке.
        </p>
      </section>

      <section id="demo" style={demoSectionStyles}>
        <div style={demoWrapperStyles}>
          <div style={demoPanelStyles}>
            <div style={{ textAlign: "center", marginBottom: "3rem" }}>
              <button style={demoHeaderButtonStyles}>ДЕМОНСТРАЦИЯ РАБОТЫ</button>
            </div>
            <h2 style={demoTitleStyles}>ДЕМОНСТРАЦИЯ РАБОТЫ СЕРВИСА</h2>
            <p style={demoSubtitleStyles}>
              Посмотрите, как работает автоматизация со стороны ученика
            </p>

            <div style={demoContentGridStyles}>
            <div style={demoLeftColumnStyles}>
              <h3 style={demoLeftTitleStyles}>Простой процесс подписания:</h3>
              {demoSteps.map((step) => (
                <div key={step.number} style={demoStepCardStyles}>
                  <div style={demoStepNumberStyles}>{step.number}</div>
                  <div style={demoStepContentStyles}>
                    <p style={demoStepTitleStyles}>{step.title}</p>
                    <p style={demoStepTextStyles}>{step.text}</p>
                  </div>
                </div>
              ))}
              <button style={demoButtonStyles}>
                Протестировать сервис бесплатно
              </button>
              <p style={demoButtonTextStyles}>
                Мы подключим демо-аккаунт и вы попробуете подписать документ на
                реальном примере
              </p>
            </div>

            <div style={demoRightColumnStyles}>
              <h3 style={demoRightTitleStyles}>
                Попробуйте подписать прямо сейчас
              </h3>
              <p style={demoRightInstructionStyles}>
                Поставьте подпись ниже (мышью или пальцем)
              </p>
              <canvas
                ref={setSignatureCanvas}
                style={demoSignatureCanvasStyles}
              />
              <p style={demoDisclaimerStyles}>
                Подпись должна соответствовать документу. Юридическая значимость
                зависит от политики вашей организации
              </p>
            </div>
          </div>
        </div>
      </div>
      </section>

      <section id="target" style={targetSectionStyles}>
        <button style={targetHeaderButtonStyles}>ДЛЯ КОГО ПОДХОДИТ</button>
        <h2 style={targetTitleStyles}>КОМУ ПОДХОДИТ СЕРВИС</h2>
        <p style={targetSubtitleStyles}>
          Наши решения идеально подходят для образовательных организаций
        </p>

        <div style={targetCardsGridStyles}>
          {targetCards.map((card, idx) => (
            <div key={idx} style={targetCardStyles}>
              {card.popular && (
                <div style={targetCardPopularTagStyles}>ПОПУЛЯРНЫЙ</div>
              )}
              <h3 style={targetCardTitleStyles}>
                <img
                  src="/free-icon-font-check-circle.png"
                  alt="Checkmark"
                  style={targetCardIconStyles}
                />
                {card.title}
              </h3>
              <p style={targetCardDescriptionStyles}>{card.description}</p>
              <ul style={targetCardListStyles}>
                {card.features.map((feature, featureIdx) => (
                  <li key={featureIdx} style={targetCardListItemStyles}>
                    <img
                      src="/free-icon-font-check-circle.png"
                      alt="Checkmark"
                      style={targetCardListIconStyles}
                    />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div style={targetResultWrapperStyles}>
          <h3 style={targetResultTitleStyles}>РЕЗУЛЬТАТ</h3>
          <p style={targetResultTextStyles}>
            Сервис помогает сократить время на оформление документов с нескольких
            часов до пары минут — без ошибок и повторных проверок
          </p>
        </div>
      </section>

      <section id="faq" style={faqSectionStyles}>
        <button style={faqHeaderButtonStyles}>ЧАСТО ЗАДАВАЕМЫЕ ВОПРОСЫ</button>
        <h2 style={faqTitleStyles}>ОТВЕТЫ НА ПОПУЛЯРНЫЕ ВОПРОСЫ</h2>
        <p style={faqSubtitleStyles}>
          Все, что вам нужно знать о нашем сервисе автоматизации документооборота
        </p>

        <div style={faqAccordionStyles}>
          {faqItems.map((item, index) => {
            const isActive = activeFaq === index;
            return (
              <div key={index} style={faqItemStyles}>
                <div
                  style={faqQuestionStyles(isActive)}
                  onClick={() => toggleFaq(index)}
                >
                  <span>{item.question}</span>
                  <span style={faqIconStyles(isActive)}>+</span>
                </div>
                <div style={faqAnswerStyles(isActive)}>
                  <p style={faqAnswerTextStyles}>{item.answer}</p>
                </div>
              </div>
            );
          })}
        </div>

        <div style={faqCtaStyles}>
          <h4 style={faqCtaTitleStyles}>
            Хотите убедиться, будет ли работать сервис в вашем организации?
          </h4>
          <p style={faqCtaTextStyles}>
            Протестируйте бесплатно — мы подключим демо-аккаунт, и вы попробуете
            подписать документ на реальном примере
          </p>
          <button style={faqCtaButtonStyles}>
            Протестировать сервис бесплатно
          </button>
        </div>
      </section>

      {modalMounted && (
        <div style={modalWrapperStyles}>
          <div style={modalStyles}>
            <p style={headingStyles}>
              АВТОМАТИЗИРУЙТЕ УЧЕБНЫЙ ДОКУМЕНТООБОРОТ
              <br />
              ЗА 1 ДЕНЬ
            </p>

            <p style={leadStyles}>
              Договоры и приказы формируются и хранятся автоматически.
              Юридически корректно, удобно и готово к проверке.
            </p>

            <div style={dashedBoxStyles}>
              Для онлайн-школ, образовательных центров и ИП, оказывающих
              образовательные услуги
            </div>

            <button style={ctaStyles}>Узнать больше</button>
          </div>
        </div>
      )}
    </div>
  );
}
